import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { User } from '../data/mockData';
import { createMeasurementViaTag } from '../services/measurementService';
import MeasurementNameInput from '../components/MeasurementForm/MeasurementNameInput';
import AdvertiserInput from '../components/MeasurementForm/AdvertiserInput';
import BrandInput from '../components/MeasurementForm/BrandInput';
import MeasurementDateRangeInputs, {
  nextUtcDay,
  utcTodayIso
} from '../components/MeasurementForm/MeasurementDateRangeInputs';
import PendingCreativesField from '../components/MeasurementForm/PendingCreativesField';
import { usePendingCreatives } from '../hooks/usePendingCreatives';
import './CreateMeasurementPage.css';

const CreateMeasurementTagPage: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [name, setName] = useState('');
  const [advertiser, setAdvertiser] = useState('');
  const [brand, setBrand] = useState<string | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string>('');
  const {
    creatives,
    error: fileError,
    addCreative,
    removeCreative,
    canSubmit,
  } = usePendingCreatives();
  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  const displayError = error || fileError;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Please enter a measurement name');
      return;
    }

    if (!advertiser.trim()) {
      setError('Please enter an advertiser');
      return;
    }

    if (creatives.length === 0) {
      setError('Please add at least one creative');
      return;
    }

    if (!startDate || !endDate) {
      setError('Start date and end date are required');
      return;
    }

    const minStart = nextUtcDay(utcTodayIso());
    if (startDate < minStart) {
      setError('Start date must be in the future (after today, UTC)');
      return;
    }
    if (endDate <= startDate) {
      setError('End date must be after start date');
      return;
    }

    setIsCreating(true);
    setError('');

    try {
      const result = await createMeasurementViaTag(
        name,
        advertiser,
        brand || null,
        startDate,
        endDate,
        creatives
      );
      if (result.acceptedAsync) {
        navigate('/measurements');
        return;
      }
      navigate(`/measurements/${result.measurementId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create measurement. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  if (!user) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <Layout user={user}>
      <div className="create-measurement-page">
        <h1>Create Measurement via Tag Collection</h1>

        <form onSubmit={handleSubmit} className="measurement-form">
          <MeasurementNameInput
            value={name}
            onChange={setName}
            disabled={isCreating}
          />

          <AdvertiserInput
            value={advertiser}
            onChange={setAdvertiser}
            disabled={isCreating}
          />

          <BrandInput
            value={brand}
            onChange={(value) => setBrand(value)}
            disabled={isCreating}
          />

          <MeasurementDateRangeInputs
            startDate={startDate}
            endDate={endDate}
            onStartChange={(v) => {
              setStartDate(v);
              setError('');
            }}
            onEndChange={(v) => {
              setEndDate(v);
              setError('');
            }}
            disabled={isCreating}
          />

          <PendingCreativesField
            creatives={creatives}
            onAdd={addCreative}
            onRemove={removeCreative}
            disabled={isCreating}
          />

          {displayError && (
            <div className="error-message">
              {displayError}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/measurements')}
              className="cancel-button"
              disabled={isCreating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="create-button"
              disabled={
                isCreating ||
                !name.trim() ||
                !advertiser.trim() ||
                !startDate ||
                !endDate ||
                creatives.length === 0 ||
                !canSubmit
              }
            >
              {isCreating ? 'Creating...' : 'Create Measurement'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default CreateMeasurementTagPage;
